<script setup lang="ts">
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import type { IOrdersForm } from '@/domain/store-setup/orders-setup';
  import {
    ORDER_TYPE_OPTIONS,
    isOrderTypeOn,
    toggleMenuOnly,
    toggleOrderType,
  } from '@/domain/store-setup/orders-step';
  import type {
    IOrderTypeOption,
    TOrdersFormPatch,
  } from '@/domain/store-setup/orders-step';

  /**
   * «¿Cómo le hacen pedidos?» (plan §4.8 item 1): three toggle cards (tables,
   * pickup, delivery) and the separate «Por ahora, solo mostrar el menú»
   * card. While menu-only is chosen the three cards dim but stay tappable:
   * a tap clears menu-only and turns that type on.
   */
  const props = withDefaults(
    defineProps<{
      value: IOrdersForm;
      disabled?: boolean;
    }>(),
    { disabled: false }
  );
  const emit = defineEmits<{
    (e: 'patch', patch: TOrdersFormPatch): void;
  }>();

  function isOn(option: IOrderTypeOption): boolean {
    return isOrderTypeOn(props.value, option.type);
  }

  function toggleType(option: IOrderTypeOption) {
    if (!props.disabled)
      emit('patch', toggleOrderType(props.value, option.type));
  }

  function toggleOnlyMenu() {
    if (!props.disabled) emit('patch', toggleMenuOnly(props.value));
  }
</script>

<template>
  <section class="v2-setup-order-types">
    <div class="type-grid">
      <button
        v-for="option in ORDER_TYPE_OPTIONS"
        :key="option.type"
        type="button"
        class="type-card"
        :class="{ 'is-on': isOn(option), 'is-dimmed': value.menuOnly }"
        :aria-pressed="isOn(option) ? 'true' : 'false'"
        :aria-disabled="value.menuOnly ? 'true' : undefined"
        :disabled="disabled"
        @click="toggleType(option)"
      >
        <span class="type-tick" aria-hidden="true">
          <PosUiIcon name="check" :size="16" />
        </span>
        <span class="type-icon" aria-hidden="true">
          <PosUiIcon :name="option.icon" :size="20" />
        </span>
        <span class="type-title">{{ $t(option.titleKey) }}</span>
        <span class="type-text">{{ $t(option.textKey) }}</span>
      </button>
    </div>
    <button
      type="button"
      class="menu-only"
      :class="{ 'is-on': value.menuOnly }"
      :aria-pressed="value.menuOnly ? 'true' : 'false'"
      :disabled="disabled"
      @click="toggleOnlyMenu"
    >
      <span class="menu-only-radio" aria-hidden="true" />
      <span class="menu-only-copy">
        <span class="menu-only-title">{{
          $t('storeSetup.orders.menuOnly')
        }}</span>
        <span class="menu-only-text">{{
          $t('storeSetup.orders.menuOnlyText')
        }}</span>
      </span>
    </button>
  </section>
</template>

<style scoped lang="scss">
  .v2-setup-order-types {
    @apply tw-flex tw-flex-col tw-gap-3 tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-p-4 md:tw-p-5;
    .type-grid {
      @apply tw-grid tw-grid-cols-1 tw-gap-2 sm:tw-grid-cols-3;
    }
    .type-card {
      @apply tw-relative tw-flex tw-min-h-11 tw-min-w-0 tw-cursor-pointer tw-flex-col tw-items-start tw-gap-1 tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-p-3.5 tw-pr-10 tw-text-left tw-transition-colors tw-duration-150 hover:tw-border-neutral-300;
      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
      &:disabled {
        @apply tw-cursor-not-allowed;
      }
      .type-tick {
        @apply tw-absolute tw-right-3 tw-top-3 tw-flex tw-h-[22px] tw-w-[22px] tw-items-center tw-justify-center tw-rounded-md tw-border-[1.5px] tw-border-solid tw-border-neutral-300 tw-bg-white tw-text-transparent;
      }
      .type-icon {
        @apply tw-mb-1 tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-lg tw-bg-neutral-100 tw-text-neutral-700;
      }
      .type-title {
        @apply tw-text-[15px] tw-font-medium tw-text-neutral-900;
      }
      .type-text {
        @apply tw-text-xs tw-text-neutral-500;
      }
      // Active state: the orange indicator of a chosen option.
      &.is-on {
        @apply tw-border-orange-600 tw-bg-orange-50 hover:tw-border-orange-600;
        .type-tick {
          @apply tw-border-orange-600 tw-bg-orange-600 tw-text-white;
        }
        .type-icon {
          @apply tw-bg-white tw-text-orange-600;
        }
      }
      &.is-dimmed {
        @apply tw-opacity-50;
      }
    }
    .menu-only {
      @apply tw-flex tw-min-h-11 tw-w-full tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-xl tw-border tw-border-dashed tw-border-neutral-300 tw-bg-white tw-px-3.5 tw-py-3 tw-text-left tw-transition-colors tw-duration-150 hover:tw-border-neutral-400;
      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
      &:disabled {
        @apply tw-cursor-not-allowed;
      }
      .menu-only-radio {
        @apply tw-h-[18px] tw-w-[18px] tw-shrink-0 tw-rounded-full tw-border-[1.5px] tw-border-solid tw-border-neutral-300 tw-bg-white;
      }
      .menu-only-copy {
        @apply tw-flex tw-min-w-0 tw-flex-col;
      }
      .menu-only-title {
        @apply tw-text-sm tw-font-medium tw-text-neutral-900;
      }
      .menu-only-text {
        @apply tw-text-xs tw-text-neutral-500;
      }
      &.is-on {
        @apply tw-border-solid tw-border-neutral-900 hover:tw-border-neutral-900;
        .menu-only-radio {
          @apply tw-border-[5px] tw-border-neutral-900;
        }
      }
    }
  }
</style>
